// In-page replacements for window.alert, window.confirm and window.prompt.
// notify() shows a calm toast; confirmAction() and askForText() return promises from a styled dialog.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Info, WarningCircle } from '@phosphor-icons/react';

const listeners = new Set();
let nextId = 1;

function emit(event) {
  if (!listeners.size) {
    // The feedback root is not mounted (tests or a page outside the app shell).
    if (event.type === 'confirm') event.resolve(window.confirm(event.message || event.title));
    else if (event.type === 'prompt') event.resolve(window.prompt(event.message || event.title, event.defaultValue || ''));
    else if (typeof console !== 'undefined') console.info(event.message);
    return;
  }
  listeners.forEach((listener) => listener(event));
}

/** Show a short message. tone: 'info' | 'success' | 'warning' | 'error'. */
export function notify(message, { tone = 'info', duration } = {}) {
  emit({ type: 'toast', id: nextId++, message: String(message ?? ''), tone, duration: duration ?? (tone === 'error' ? 8000 : 4500) });
}

/** Ask before a destructive or irreversible action. Resolves true when confirmed. */
export function confirmAction({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'danger' } = {}) {
  return new Promise((resolve) => emit({ type: 'confirm', id: nextId++, title, message, confirmLabel, cancelLabel, tone, resolve }));
}

/** Ask for a short piece of text. Resolves the text, or null when cancelled. */
export function askForText({ title, message, label, defaultValue = '', confirmLabel = 'Continue', cancelLabel = 'Cancel' } = {}) {
  return new Promise((resolve) => emit({ type: 'prompt', id: nextId++, title, message, label, defaultValue, confirmLabel, cancelLabel, resolve }));
}

const TOAST_ICONS = { success: CheckCircle, error: WarningCircle, warning: WarningCircle, info: Info };

function Toasts({ toasts, dismiss }) {
  return (
    <div className="rc-toasts" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => {
        const Icon = TOAST_ICONS[toast.tone] || Info;
        return (
          <div key={toast.id} className={`rc-toast rc-toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'} onClick={() => dismiss(toast.id)}>
            <Icon size={20} aria-hidden="true" />
            <span className="rc-toast__text">{toast.message}</span>
          </div>
        );
      })}
    </div>
  );
}

function Question({ request, onDone }) {
  const [text, setText] = useState(request.defaultValue || '');
  const confirmButton = useRef(null);
  const field = useRef(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    (request.type === 'prompt' ? field.current : confirmButton.current)?.focus();
    const onKey = (event) => { if (event.key === 'Escape') done.current(request.type === 'prompt' ? null : false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request]);
  const submit = (event) => {
    event.preventDefault();
    onDone(request.type === 'prompt' ? text : true);
  };
  return createPortal(
    <div className="rc-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onDone(request.type === 'prompt' ? null : false); }}>
      <form className="rc-dialog" role="dialog" aria-modal="true" aria-labelledby={`rc-q-${request.id}`} onSubmit={submit}>
        <div className="rc-dialog__head" id={`rc-q-${request.id}`}>{request.title || 'Are you sure?'}</div>
        <div className="rc-dialog__body rc-stack" style={{ gap: 12 }}>
          {request.message ? <p style={{ margin: 0, color: 'var(--rc-text-2)', whiteSpace: 'pre-line' }}>{request.message}</p> : null}
          {request.type === 'prompt' ? (
            <label className="rc-field">
              {request.label ? <span className="rc-label">{request.label}</span> : null}
              <input ref={field} value={text} onChange={(event) => setText(event.target.value)} autoComplete="off" />
            </label>
          ) : null}
        </div>
        <div className="rc-dialog__actions">
          <button type="button" className="rc-btn" onClick={() => onDone(request.type === 'prompt' ? null : false)}>{request.cancelLabel}</button>
          <button ref={confirmButton} type="submit" className={`rc-btn ${request.tone === 'danger' ? 'rc-btn--danger' : 'rc-btn--primary'}`}>{request.confirmLabel}</button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

/** Mount once near the root of the app. */
export function FeedbackRoot() {
  const [toasts, setToasts] = useState([]);
  const [questions, setQuestions] = useState([]);
  useEffect(() => {
    const listener = (event) => {
      if (event.type === 'toast') {
        setToasts((current) => [...current.slice(-3), event]);
        window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== event.id)), event.duration);
      } else {
        setQuestions((current) => [...current, event]);
      }
    };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  const current = questions[0];
  return (
    <>
      <Toasts toasts={toasts} dismiss={(id) => setToasts((list) => list.filter((toast) => toast.id !== id))} />
      {current ? (
        <Question
          key={current.id}
          request={current}
          onDone={(value) => { current.resolve(value); setQuestions((list) => list.slice(1)); }}
        />
      ) : null}
    </>
  );
}
